<template>
	<form class="devices" @submit.prevent="onSubmit">
		<div id="contentMain" class="deviceCheck mydoc2018 signersPage">
			<div class="myDocuments">
				<div class="deviceContainer">
					<h1 id="myDocsTitle">
						Set Up Devices
					</h1>
					<div class="setUpContainer">
						<p class="mainText">
							Ensure your devices are working properly to continue.
						</p>

						<div class="setUpWrapper">
							<div class="vidContainer">
								<div v-if="data.camera.failure" class="noCamera">
									<p class="err">
										No camera found. Allow access to your camera, check the installation, or try a different device.
									</p>
								</div>

								<span 
									v-if="data.camera.list.length == 0 && !data.camera.failure"
									class="uploadCircle"
								/>
								<video
									v-if="!data.camera.failure"
									ref="cameraVideo"
									class="cameraImage"
									autoplay
									playsinline
								/>
							</div>
							<div class="deviceContainer">
								<div class="deviceType camera">
									<label>Camera:</label>
									<select v-model="model.camera.selected">
										<option :value="null">
											None selected
										</option>
										<option
											v-for="camera in data.camera.list"
											:key="camera.deviceId"
											:value="camera.deviceId"
										>
											{{ resizeLabel(camera.label) }}
										</option>
									</select>

									<a class="retry" @click="loadCamerasAsync()" />
								</div>

								<div class="deviceType mic">
									<label>Microphone:</label>
									<select v-model="model.microphone.selected">
										<option :value="null">
											None selected
										</option>
										<option
											v-for="microphone in data.microphone.list"
											:key="microphone.deviceId"
											:value="microphone.deviceId"
										>
											{{ resizeLabel(microphone.label) }}
										</option>
									</select>

									<a class="retry" @click="loadMicrophonesAsync()" />
								</div>

								<div class="progressWrap">
									<span class="micIcon" /> <progress
										class="deviceTest"
										:value="model.microphone.audioLevel"
										max="100"
									/>
								</div>

								<div class="errWrap">
									<p v-if="data.microphone.failure" class="err">
										No mic found. Allow access to your mic, check the installation, or try a different
										device.
									</p>
								</div>

								<div class="deviceType audio">
									<label>Audio:</label>
									<audio
										ref="audioTest"
										:src="`${urlBase}/audio/AudioTest.wav`"
										preload="auto"
										@ended="stopSpeakersTest()"
									/>
									<a class="newButton testButton" @click="speakersTestEventAsync">{{
										data.speakers.playButtonText
									}}</a>
								</div>

								<div class="progressWrap">
									<span class="audioIcon" /> <progress
										ref="audioBar"
										class="deviceTest"
										:value="model.speakers.audioLevel"
										max="100"
									/>
								</div>
							</div>
						</div>

						<div class="continueContainer desktop">
							<a
								class="previousButton"
								:class="{ 'ui-state-disabled': !canCancel }"
								:href="cancelHref"
								@click="$emit('previous')"
							>Back</a>
							<button
								:class="enableContinue ? 'largeProceedButton' : 'ui-state-disabled largeProceedButton'"
								:disabled="!enableContinue"
								@click="$emit('confirm')"
							>
								Continue
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	</form>
</template>

<script>
import { mapActions } from "vuex";


async function loadCamerasAsync() {
	try {
		let devices = await navigator.mediaDevices.enumerateDevices();
		this.data.camera.list = devices.filter((device) => device.kind === `videoinput` && device.deviceId !== ``);

		if (!this.model.camera.selected) {
			//since we suggested to get the Front camera when we accessed the stream try to get the name of the connected device and match on that first
			let defaultCamera = null;
			//if we didn't find the front camera then try to find the default otherwise use first one in list
			if (defaultCamera == null) {
				defaultCamera = this.data.camera.list.find((cam) => cam.deviceId === `default`) ?? this.data.camera.list[0];

				this.model.camera.checked = false;
			}

			if (this.model.camera.stream) {
				let videoTrack = this.model.camera.stream.getVideoTracks()[0];
				defaultCamera = this.data.camera.list.find((cam) => cam.label == videoTrack.label);
			}
			
			this.model.camera.selected = defaultCamera.deviceId;
			this.model.camera.checked = true;
		}
		else {
			//The first section of the IF will set the selected property in which there is a watch which will trigger connectCameraAsync
			//on its own.  This is only needs to fire if refresh is clicked, when that is clicked we do reload the devices but we do not
			//change the selected value so we will need to call connectCameraAsync
			await this.connectCameraAsync();

			if (this.model.camera.checked === true &&
				this.model.microphone.checked === true
			) {
				this.allChecked = true;
			}
		}
	}
	catch (ex) {
		this.data.camera.failure = true;
		this.model.camera.checked = false;
		this.allChecked = false;
		this.trackCameraException(ex);
	}
}

async function connectCameraAsync() {
	try {
		this.data.camera.failure = false;

		if (this.model.camera.stream) {
			this.disconnect(this.model.camera.stream);
		}
		if (this.model.camera.selected) {
			this.model.camera.stream = await navigator.mediaDevices.getUserMedia({ 'video': { deviceId: this.model.camera.selected }, 'audio': false });

			this.$refs.cameraVideo.srcObject = this.model.camera.stream;
			this.model.camera.checked = true;
	
			if (this.model.camera.checked == true &&
				this.model.microphone.checked == true
			) {
				this.allChecked = true;
			}
		} else {
			//we have no camera selected
			this.model.camera.checked = false;
			this.data.camera.failure = true;
			this.allChecked = false;
		}
	}
	catch (ex) {
		this.model.camera.checked = false;
		this.data.camera.failure = true;
		this.allChecked = false;
		this.trackCameraException(ex);
	}
}

function loadSpeakers() {
	try {
		const audioCtx = new AudioContext();
		this.model.speakers.analyser = audioCtx.createAnalyser();
		this.model.speakers.analyser.fftSize = 32;
		this.model.speakers.analyser.connect(audioCtx.destination);
		let source = audioCtx.createMediaElementSource(this.$refs.audioTest);
		source.connect(this.model.speakers.analyser);

		this.data.speakers.failure = false;
		this.model.speakers.loaded = true;
		this.model.speakers.checked = true;
	}
	catch (ex) {
		this.model.speakers.checked = false;
		this.data.speakers.failure = true;
		this.trackSpeakersException(ex);
	}
}

async function speakersTestEventAsync() {
	this.model.speakers.checked = true;
	if (this.$refs.audioTest.paused) {
		await this.startSpeakersTestAsync();
	} else {
		this.stopSpeakersTest();
	}
}

async function startSpeakersTestAsync() {
	//Earlier versions of the code had this happening when the scene changed however this appeared to cause problems with running the test and capturing
	//the volume so moving it to the button press
	if (!this.model.speakers.loaded)
		this.loadSpeakers();

	this.model.speakers.frameRef = window.requestAnimationFrame(this.updateSpeakersBar);

	let audio = this.$refs.audioTest;
	audio.currentTime = 0;
	await audio.play();

	this.data.speakers.playButtonText = `Stop`;
}

function stopSpeakersTest() {
	let audio = this.$refs.audioTest;
	audio.pause();
	audio.load();
	this.model.speakers.audioLevel = 0;
	this.data.speakers.playButtonText = `Test audio`;
	window.cancelAnimationFrame(this.model.speakers.frameRef);
}

function updateSpeakersBar() {
	if (this.model.speakers.analyser) {
		// WebRTC outputs sample data from 0~128~255, with 128 being "zero"
		const timeByteData = new Uint8Array(this.model.speakers.analyser.frequencyBinCount);
		this.model.speakers.analyser.getByteTimeDomainData(timeByteData);
		// Absolute
		// So first we have to center it to -128~0~128, then Absolute it to 0~128
		const absData = timeByteData.map(x => Math.abs(x - 128));
		// And then take the loudest FFT Bin as our "volume", and finally normalize it to DB100 (0~100)
		const level = Math.round(Math.max(...absData) / 1.28);
		this.model.speakers.audioLevel = level;
	}

	this.model.speakers.frameRef = window.requestAnimationFrame(this.updateSpeakersBar);
}

async function loadMicrophonesAsync() {
	try {
		let devices = await navigator.mediaDevices.enumerateDevices();
		this.data.microphone.list = devices.filter((device) => device.kind === `audioinput` && device.deviceId !== ``);		

		if (!this.model.microphone.selected) {
			//since we suggested to get the Front microphone when we accessed the stream try to get the name of the connected device and match on that first
			let defaultMic = null;

			//if we didn't find the front microphone then try to find the default otherwise use first one in list
			if (defaultMic == null) {
				defaultMic = this.data.microphone.list.find((mic) => mic.deviceId === `default`) ?? this.data.microphone.list[0];
			}

			this.model.microphone.selected = defaultMic.deviceId;
			this.model.microphone.checked = true;
			
		} else {
			await this.connectMicrophoneAsync();

			if (this.model.camera.checked === true &&
				this.model.microphone.checked === true
			) {
				this.allChecked = true;
			}
		}

		this.model.microphone.audioCtx = new AudioContext();
		this.model.microphone.analyser = this.model.microphone.audioCtx.createAnalyser();
		this.model.microphone.analyser.fftSize = 32;
	} catch (ex) {
		this.model.microphone.checked = false;
		this.data.microphone.failure = true;
		this.allChecked = false;
		this.trackMicException(ex);
	}
}

async function connectMicrophoneAsync() {
	try {
		this.data.microphone.failure = false;

		if (this.model.microphone.stream) {
			this.disconnect(this.model.microphone.stream);
		}
		if (this.model.microphone.selected) {
			this.model.microphone.stream = await navigator.mediaDevices.getUserMedia({ 'video': false, 'audio': { deviceId: this.model.microphone.selected } });
			
			this.model.microphone.checked = true;

			if (this.model.camera.checked == true &&
				this.model.microphone.checked == true
			) {
				this.allChecked = true;
			}
		}
		else {
			//we have no microphone selected
			this.data.microphone.failure = true;
			this.model.microphone.checked = false;
			this.allChecked = false;
		}

		this.stopMicTest();		

		this.model.microphone.outNode = this.model.microphone.audioCtx.createMediaStreamSource(this.model.microphone.stream);

		this.startMicTest();

	} catch (ex) {
		this.model.microphone.checked = false;
		this.data.microphone.failure = true;
		this.allChecked = false;
		this.trackMicException(ex);
	}
}

function startMicTest() {
	if (this.model.microphone.selected) {
		this.model.microphone.outNode.connect(this.model.microphone.analyser);
		this.model.microphone.frameRef = window.requestAnimationFrame(this.updateMicrophoneBar);
		this.data.microphone.timeoutFrom = Date.now();
		this.checkMicrophoneTimeout();		
	}
}

function stopMicTest() {
	this.data.microphone.timeoutFrom = null;
	window.clearTimeout(this.data.microphone.timeoutThread);

	if (this.model.microphone.outNode) {
		this.model.microphone.outNode.disconnect();
	}

	window.cancelAnimationFrame(this.model.microphone.frameRef);
	this.updateMicrophoneBar();
}

function updateMicrophoneBar() {
	if (this.model.microphone.analyser) {
		// WebRTC outputs sample data from 0~128~255, with 128 being "zero"
		const timeByteData = new Uint8Array(this.model.microphone.analyser.frequencyBinCount);
		this.model.microphone.analyser.getByteTimeDomainData(timeByteData);
		// Absolute
		// So first we have to center it to -128~0~128, then Absolute it to 0~128
		const absData = timeByteData.map(x => Math.abs(x - 128));
		// And then take the loudest FFT Bin as our "volume", and finally normalize it to DB100 (0~100)
		const level = Math.round(Math.max(...absData) / 1.28);
		this.model.microphone.audioLevel = level;
		this.data.microphone.timeoutFrom = Date.now();
	}

	this.model.microphone.frameRef = window.requestAnimationFrame(this.updateMicrophoneBar);
}

function checkMicrophoneTimeout() {
	if (this.data.microphone.timeoutFrom) {
		const now = Date.now();
		const diff = now - this.data.microphone.timeoutFrom;
		// Give 10sec to confirm mic
		if (diff > 10000) {
			this.model.microphone.checked = false;
		}
		this.data.microphone.timeoutThread = window.setTimeout(this.checkMicrophoneTimeout, 1000);
	}
}

function disconnect(stream) {
	if (!stream) {
		return;
	}
	stream.getTracks().forEach(function (track) {
		track.stop();
	});
}

function onSubmit() {
	const config = {
		camera: this.model.camera.selected,
		microphone: this.model.microphone.selected
	};
	this.$emit(`finish`, config);
}

function resizeLabel(labelValue) {
	if (labelValue.length > 40) {
		let newValue = labelValue.substring(0, 40);
		return newValue + ` ...`;
	}
	return labelValue;
}

function trackEvent(event) {
	this.$emit(`trackedEvent`, event);
}

function trackCameraEvent(action, extra) {
	this.trackEvent({ component: `Camera Test`, action, extra });
}

function trackCameraException(extra) {
	this.trackCameraEvent(`Unknown Failure`, extra);
}

function trackSpeakersException(extra) {
	this.trackEvent({ component: `Speaker Test`, action: `Unknown Failure`, extra });
}

function trackMicEvent(action, extra) {
	this.trackEvent({ component: `Microphone Test`, action, extra });
}

function trackMicException(extra) {
	this.trackMicEvent(`Unknown Failure`, extra);
}

export default {
	props: {
		finishText: { type: String, required: true },
		cancelHref: { type: String, required: true },
		canCancel: { type: Boolean, required: false, default: true },		
		enableDebug: { type: Boolean, required: false },
	},

	emits: [`finish`, `trackedEvent`, `previous`, `confirm`],
	data() {
		return {
			urlBase: null,
			DebugMode: false,
			allChecked: false,			
			data: {
				camera: {
					list: [],
					failure: false,
					notarizationId: null,
				},
				speakers: {
					list: [],
					playButtonText: `Test audio`,
					failure: false
				},
				microphone: {
					list: [],
					failure: false,
					timeoutThread: null
				},
				//cancellationToken: null,
			},
			model: {
				camera: {
					selected: null,
					checked: false,
					stream: null,
				},
				speakers: {
					analyser: null,
					audioLevel: 0,
					checked: false,
					audioContext: null,
					testTrack: null,
					frameRef: 0,
					loaded: false
				},
				microphone: {
					selected: null,
					audioCtx: null,
					analyser: null,
					audioLevel: 0,
					checked: false,
					stream: null,
					frameRef: 0,
					timeoutFrom: null
				},
			},
		};
	},
	computed: {
		enableContinue() {
			return this.DebugMode || this.allChecked;
		}
	},
	watch: {
		'model.camera.selected'() {
			this.connectCameraAsync();

		},
		'model.microphone.selected'() {
			this.connectMicrophoneAsync();
		},
		
	},
	created() {
		this.urlBase = window.urlBase; //Needs to be done here otherwise there is a small window where the path for camera.png is incorrect.
	},
	mounted() {
		this.notarizationId = window.Notary.NotarizationId;
		
		window.AudioContext = window.AudioContext || window.webkitAudioContext;

		this.DebugMode = window.DebugMode || this.enableDebug;
		
		// Get user consent for devices to start
		//We return true on reject to prevent console error when there is no cameras available
		navigator.mediaDevices.getUserMedia({ 'video': { facingMode: `user` }, 'audio': true })
			.then((videoStream) => { this.model.camera.stream = videoStream; }, () => { this.trackCameraEvent(`No Camera`, `No camera or permission denied`); return true; })
			.then(() => this.loadCamerasAsync(), (reason) => { this.trackCameraException(reason); return true; }).then(() => this.loadMicrophonesAsync(), (reason) => { this.trackMicException(reason); return true; });
	},

	methods: {
		...mapActions([`goToNextIncompleteSignerProfile`]),
		
		finishDeviceCheck(config) {
			sessionStorage[`DeviceCheckConfig`] = JSON.stringify(config);
			this.goToNextIncompleteSignerProfile();
		},

		disconnect,
		loadCamerasAsync, connectCameraAsync,
		loadSpeakers, speakersTestEventAsync, startSpeakersTestAsync, stopSpeakersTest, updateSpeakersBar,
		loadMicrophonesAsync, connectMicrophoneAsync, startMicTest, stopMicTest, updateMicrophoneBar, checkMicrophoneTimeout,
		onSubmit, resizeLabel,
		trackEvent, trackCameraEvent, trackCameraException, trackSpeakersException, trackMicEvent, trackMicException
	}
};
</script>